/**
 * FlexContainer-specific spatial indexing hook
 *
 * NOTE: Spatial indexing has been removed as TerraMatrix is now AI-chat-only.
 * This hook now returns stub values for backward compatibility.
 * All components use flex-based layout, eliminating the need for QuadTree spatial indexing.
 */

import React, { useCallback, useMemo } from 'react';
import type { Component } from '@/types';

// Stub type for backward compatibility (was from SpatialIndexingSystem)
export interface ComponentBounds {
  id: string;
  x: number;
  y: number;
  z: number;
  width: number;
  height: number;
  type?: string;
  lastUpdated?: number;
  nestingDepth?: number;
}

export interface FlexContainerProperties {
  flexDirection: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  justifyContent: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
  alignItems: 'stretch' | 'flex-start' | 'center' | 'flex-end' | 'baseline';
  flexWrap: 'nowrap' | 'wrap' | 'wrap-reverse';
  gap: number;
  padding: number;
  backgroundColor: string;
  borderRadius: number;
  border?: string;
  minHeight: number;
  label?: string;
}

export interface FlexContainerSpatialConfig {
  /** FlexContainer component instance */
  component: Component;
  /** Container position on canvas */
  position: { x: number; y: number; z: number };
  /** Container size */
  size: { width: number; height: number };
  /** Flex properties for bounds calculation */
  properties: FlexContainerProperties;
  /** Child components */
  children: Component[];
  /** Container DOM reference for precise bounds calculation */
  containerRef?: React.RefObject<HTMLDivElement>;
  /** Enable child spatial tracking */
  trackChildren?: boolean;
  /** Enable collision detection for container and children */
  enableCollisions?: boolean;
}

export interface ChildSpatialBounds extends ComponentBounds {
  /** Index in flex layout */
  flexIndex: number;
  /** Parent container ID */
  parentContainerId: string;
  /** Computed flex properties */
  computedFlex?: {
    flexBasis: number;
    flexGrow: number;
    flexShrink: number;
    alignSelf: string;
  };
}

export interface FlexContainerSpatialResult {
  /** Container spatial bounds */
  containerBounds: ComponentBounds;
  /** Child components spatial bounds */
  childrenBounds: ChildSpatialBounds[];
  /** Check collisions for container */
  checkContainerCollisions: () => ComponentBounds[];
  /** Check collisions for all children */
  checkChildrenCollisions: () => Map<string, ComponentBounds[]>;
  /** Query components in container area */
  queryContainerArea: (expandBy?: number) => ComponentBounds[];
  /** Update container bounds manually */
  updateContainerBounds: (newBounds: Partial<ComponentBounds>) => void;
  /** Update child bounds manually */
  updateChildBounds: (childId: string, newBounds: Partial<ChildSpatialBounds>) => void;
  /** Get container spatial metrics */
  getSpatialMetrics: () => ContainerSpatialMetrics;
}

export interface ContainerSpatialMetrics {
  /** Container area utilization */
  areaUtilization: number;
  /** Number of child collisions */
  childCollisionCount: number;
  /** Container spatial density */
  spatialDensity: number;
  /** Average child spacing */
  averageChildSpacing: number;
  /** Container bounds efficiency */
  boundsEfficiency: number;
}

/**
 * Hook for FlexContainer-specific spatial operations
 *
 * NOTE: This is now a stub implementation. Spatial indexing has been removed
 * as TerraMatrix uses AI-chat-only interaction with flex-based layouts.
 */
export function useFlexContainerSpatial(
  config: FlexContainerSpatialConfig,
  _spatialSystem?: unknown // Kept for backward compatibility, ignored
): FlexContainerSpatialResult {
  const {
    component,
    position,
    size,
    properties,
    children,
    containerRef,
  } = config;

  // Calculate container bounds (simplified - no spatial indexing)
  const containerBounds = useMemo<ComponentBounds>(() => {
    let calculatedWidth = size.width;
    let calculatedHeight = size.height;

    // Get actual rendered dimensions if available
    if (children.length > 0 && containerRef?.current) {
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        calculatedWidth = rect.width;
        calculatedHeight = rect.height;
      }
    } else {
      calculatedHeight = Math.max(size.height, properties.minHeight);
    }

    return {
      id: component.id,
      x: position.x,
      y: position.y,
      z: position.z,
      width: calculatedWidth,
      height: calculatedHeight,
      type: 'flex-container',
      lastUpdated: Date.now(),
      nestingDepth: 0
    };
  }, [component.id, position, size, properties.minHeight, children.length, containerRef]);

  // Stub child bounds (empty - flex layout handles positioning)
  const childrenBounds = useMemo<ChildSpatialBounds[]>(() => [], []);

  // Stub collision detection (always returns empty - no collisions in flex layout)
  const checkContainerCollisions = useCallback((): ComponentBounds[] => [], []);
  const checkChildrenCollisions = useCallback((): Map<string, ComponentBounds[]> => new Map(), []);

  // Stub query function
  const queryContainerArea = useCallback((): ComponentBounds[] => [], []);

  // Stub update functions (no-op)
  const updateContainerBounds = useCallback(() => {}, []);
  const updateChildBounds = useCallback(() => {}, []);

  // Stub metrics (reasonable defaults)
  const getSpatialMetrics = useCallback((): ContainerSpatialMetrics => ({
    areaUtilization: 1,
    childCollisionCount: 0,
    spatialDensity: 0,
    averageChildSpacing: properties.gap || 16,
    boundsEfficiency: 1
  }), [properties.gap]);

  return {
    containerBounds,
    childrenBounds,
    checkContainerCollisions,
    checkChildrenCollisions,
    queryContainerArea,
    updateContainerBounds,
    updateChildBounds,
    getSpatialMetrics
  };
}

// NOTE: All spatial indexing helper functions have been removed.
// Flex layout handles positioning automatically, eliminating the need for:
// - calculateFlexContentHeight
// - calculateChildFlexBounds
// - calculateChildFlexProperties
// - hasSignificantChange
// - calculateAverageChildSpacing
// - calculateBoundsEfficiency
